// src/Components/GoogleMap/PolylineManager.js

import { useRef, useState, useCallback, useEffect } from 'react';
import { doc, deleteField, writeBatch, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../Firebase/firebase';
import { usePolylineDraw } from './PolylineDraw';

const DEFAULT_COLOR = '#00B0FF';
const DEFAULT_WEIGHT = 4;
const HIT_AREA_WEIGHT = 20;

const sanitizeForFirestore = (data) => {
  const clean = { ...data };
  Object.keys(clean).forEach((key) => {
    if (clean[key] === undefined) clean[key] = null;
  });
  return clean;
};

const deleteStorageImage = async (imageUrl) => {
  if (!imageUrl) return;
  try {
    if (imageUrl.includes('firebasestorage')) {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    }
  } catch (error) {
    if (error.code !== 'storage/object-not-found') {
      console.warn('[PolylineManager] Error deleting file from storage:', error);
    }
  }
};

const ROGUE_FIELDS = [
  'groupHasMarker',
  'groupHasPolygon',
  'groupHasPolyline',
  'initialStyleIcon',
  'icon',
  'iconID',
  'isSaved',
  'type',
  'id',
  'isDrawing',
  'isDrawingLine',
  'isPlacingMarker',
  'path',
  'paths',
  'position',
  'color',
  'weight',
  'radius',
  'geometry',
  'markerGroupID',
  'isSilentAutoSave',
  'isDashed',
  'dashColor',
  'dashLength',
  '_screenX',
  '_screenY',
  '_shiftIndex',
  '_shiftedPos',
];

const getCoord = (pt, type) => {
  if (pt == null) return NaN;
  if (type === 'lat') {
    if (typeof pt.lat === 'function') return pt.lat();
    if (pt.lat !== undefined) return pt.lat;
    if (pt.latitude !== undefined) return pt.latitude;
    if (Array.isArray(pt)) return pt[0];
  } else {
    if (typeof pt.lng === 'function') return pt.lng();
    if (pt.lng !== undefined) return pt.lng;
    if (pt.longitude !== undefined) return pt.longitude;
    if (Array.isArray(pt)) return pt[1];
  }
  return NaN;
};

export function usePolylineManager(
  mapInstance,
  polylinesData,
  setPolylinesData,
  editModeEnabled,
  setTooltip,
  onDebugUpdate,
  setSelectedMarkerData,
  globalLockRef,
  setIsSaving,
  activeMenu,
  clearAllExcept,
  setInteractionLock,
  setDrawingStyles,
  isDrawMode,
  interactionLockRef,
  activeTool,
  drawingStyles,
) {
  const [isEditingPolyline, setIsEditingPolyline] = useState(false);
  const [activePolylineId, setActivePolylineId] = useState(null);
  const activePolylineIdRef = useRef(null);

  const polylineRefs = useRef({});
  const editListenersRef = useRef([]);

  const setSelectedMarkerDataRef = useRef(setSelectedMarkerData);
  useEffect(() => {
    setSelectedMarkerDataRef.current = setSelectedMarkerData;
  }, [setSelectedMarkerData]);

  const polylinesDataRef = useRef(polylinesData);
  const originalStateRef = useRef({});

  const editModeRef = useRef(editModeEnabled);
  const activeMenuRef = useRef(activeMenu);

  const activeToolRef = useRef(activeTool);
  useEffect(() => {
    activeToolRef.current = activeTool;
  }, [activeTool]);

  const drawingStylesRef = useRef(drawingStyles);
  useEffect(() => {
    drawingStylesRef.current = drawingStyles;
  }, [drawingStyles]);

  const updateLockTimeoutRef = useRef(null);

  const clearAllExceptRef = useRef(clearAllExcept);
  useEffect(() => {
    clearAllExceptRef.current = clearAllExcept;
  }, [clearAllExcept]);

  const setTooltipRef = useRef(setTooltip);
  useEffect(() => {
    setTooltipRef.current = setTooltip;
  }, [setTooltip]);

  const isClickableRef = useRef(true);

  const isPermittedUserRef = useRef(false);
  const restrictedSettingsRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSettings = async () => {
      try {
        const restRef = doc(db, 'settings', 'RestrictedMarkerSettings');
        const restSnap = await getDoc(restRef);
        if (isMounted && restSnap.exists()) {
          restrictedSettingsRef.current = restSnap.data();
        }
      } catch (e) {
        console.error('Error fetching restricted settings', e);
      }
    };
    fetchSettings();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      if (user && user.email) {
        try {
          const emailKey = user.email.toLowerCase().trim();
          const permRef = doc(db, 'permittedUsers', emailKey);
          const permSnap = await getDoc(permRef);
          if (isMounted) {
            isPermittedUserRef.current = permSnap.exists();
          }
        } catch (e) {
          console.error('Error fetching permission', e);
        }
      } else {
        if (isMounted) {
          isPermittedUserRef.current = false;
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const setPolylinesClickable = useCallback((clickable) => {
    isClickableRef.current = clickable;
    Object.values(polylineRefs.current).forEach((obj) => {
      if (obj?.visual) obj.visual.setOptions({ clickable: clickable });
      if (obj?.hit) obj.hit.setOptions({ clickable: clickable });
    });
  }, []);

  useEffect(() => {
    setPolylinesClickable(isClickableRef.current);
  }, [isDrawMode, setPolylinesClickable]);

  useEffect(() => {
    polylinesDataRef.current = polylinesData;
    window.__polylineArray = polylinesData;
  }, [polylinesData]);
  useEffect(() => {
    editModeRef.current = editModeEnabled;
  }, [editModeEnabled]);
  useEffect(() => {
    activeMenuRef.current = activeMenu;
  }, [activeMenu]);
  useEffect(() => {
    activePolylineIdRef.current = activePolylineId;
  }, [activePolylineId]);

  const enterLineEditModeRef = useRef(null);

  const {
    isDrawingLine,
    isDrawingLineRef,
    startDrawingLine,
    cancelDrawingLine,
    handleLineDrawClick,
    handleLineMouseMove,
    completePolyline,
  } = usePolylineDraw(
    mapInstance,
    setPolylinesData,
    setSelectedMarkerData,
    onDebugUpdate,
    setActivePolylineId,
    (id) =>
      enterLineEditModeRef.current &&
      enterLineEditModeRef.current(id, 'usePolylineDraw finish'),
    drawingStyles,
  );

  const getDashIcons = (isDashed, dashColor, dashLength, weight) => {
    if (!isDashed) return [];
    const length = dashLength || 10;
    return [
      {
        icon: {
          path: 'M 0,-1 0,1',
          strokeColor: dashColor || '#000000',
          strokeWeight: weight,
          strokeOpacity: 1,
          scale: length / 2,
        },
        offset: '0',
        repeat: `${length * 2}px`,
      },
    ];
  };

  const changePolylineColor = useCallback(
    (newColor) => {
      const id = activePolylineIdRef.current;
      if (!id || !newColor) return;

      const obj = polylineRefs.current[id];
      if (!obj?.visual) return;

      const currentColor = obj.visual.metadata?.color || DEFAULT_COLOR;
      if (currentColor.toLowerCase() === newColor.toLowerCase()) return;

      obj.visual.setOptions({ strokeColor: newColor });
      obj.visual.metadata = { ...obj.visual.metadata, color: newColor };

      setPolylinesData((prev) => {
        const next = prev.map((p) => (p.id === id ? { ...p, color: newColor } : p));
        polylinesDataRef.current = next;
        return next;
      });

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current((prev) =>
          prev && prev.id === id ? { ...prev, color: newColor } : prev,
        );
      }
    },
    [setPolylinesData],
  );

  const changePolylineWeight = useCallback(
    (newWeight) => {
      const id = activePolylineIdRef.current;
      if (!id) return;

      const weightNum = parseInt(newWeight, 10) || DEFAULT_WEIGHT;

      const obj = polylineRefs.current[id];
      if (!obj?.visual) return;

      const currentWeight = parseInt(obj.visual.metadata?.weight, 10) || DEFAULT_WEIGHT;
      if (currentWeight === weightNum) return;

      const meta = obj.visual.metadata;
      const icons = getDashIcons(
        meta.isDashed,
        meta.dashColor,
        meta.dashLength,
        weightNum,
      );

      obj.visual.setOptions({
        strokeWeight: weightNum,
        icons: icons,
      });

      obj.visual.metadata = { ...meta, weight: weightNum };

      setPolylinesData((prev) => {
        const next = prev.map((p) => (p.id === id ? { ...p, weight: weightNum } : p));
        polylinesDataRef.current = next;
        return next;
      });

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current((prev) =>
          prev && prev.id === id ? { ...prev, weight: weightNum } : prev,
        );
      }
    },
    [setPolylinesData],
  );

  const changePolylineDash = useCallback(
    (isDashed, dashColor, dashLength) => {
      const id = activePolylineIdRef.current;
      if (!id) return;

      const obj = polylineRefs.current[id];
      if (!obj?.visual) return;

      const meta = obj.visual.metadata || {};
      const weight = parseInt(meta.weight || DEFAULT_WEIGHT, 10);
      const icons = getDashIcons(isDashed, dashColor, dashLength, weight);

      obj.visual.setOptions({ icons: icons });
      obj.visual.metadata = {
        ...meta,
        isDashed,
        dashColor,
        dashLength,
      };

      setPolylinesData((prev) => {
        const next = prev.map((p) =>
          p.id === id
            ? {
                ...p,
                isDashed,
                dashColor,
                dashLength,
              }
            : p,
        );
        polylinesDataRef.current = next;
        return next;
      });

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current((prev) =>
          prev && prev.id === id
            ? {
                ...prev,
                isDashed,
                dashColor,
                dashLength,
              }
            : prev,
        );
      }
    },
    [setPolylinesData],
  );

  const createHitLine = useCallback(
    (map, path, metadata, onEnterEdit) => {
      if (!window.google) return null;
      const shouldBeClickable = isClickableRef.current;

      const hitLine = new window.google.maps.Polyline({
        path: path,
        map: map,
        strokeColor: 'transparent',
        strokeOpacity: 0,
        strokeWeight: HIT_AREA_WEIGHT,
        clickable: shouldBeClickable,
        zIndex: 11,
      });
      hitLine.metadata = metadata;

      window.google.maps.event.addListener(hitLine, 'mousemove', (e) => {
        const id = hitLine.metadata?.id;
        const exists = polylinesDataRef.current.find((p) => p.id === id);
        if (!exists) {
          if (setTooltipRef.current) setTooltipRef.current(null);
          return;
        }

        if (isDrawingLineRef.current) return;
        const isActiveCur = id === activePolylineIdRef.current;
        if (!isClickableRef.current && !isActiveCur) return;

        const data = hitLine.metadata || {};
        if (setTooltipRef.current) {
          setTooltipRef.current((prev) => {
            if (prev && prev.source === 'MARKER') return prev;
            return {
              source: 'SHAPE',
              x: e.domEvent.clientX,
              y: e.domEvent.clientY,
              content: { id: data.id, image: data.hoverImage, body: data.hoverContent },
            };
          });
        }
      });

      window.google.maps.event.addListener(hitLine, 'mouseout', () => {
        if (setTooltipRef.current) {
          setTooltipRef.current((prev) => (prev?.source === 'MARKER' ? prev : null));
        }
      });

      window.google.maps.event.addListener(hitLine, 'click', (e) => {
        if (e?.stop) e.stop();
        if (e?.domEvent) {
          e.domEvent.stopPropagation();
          e.domEvent.preventDefault();
        }

        const id = hitLine.metadata?.id;
        const exists = polylinesDataRef.current.find((p) => p.id === id);
        if (!exists) return;

        window.__shapeClickLock = true;
        setTimeout(() => {
          window.__shapeClickLock = false;
        }, 300);

        if (isDrawingLineRef.current) return;

        const isActiveCur = id === activePolylineIdRef.current;
        if (!isClickableRef.current && !isActiveCur) return;

        if (
          !editModeRef.current &&
          !['MARKUP_MARKERS', 'HOME', 'VIEW_INFO', 'UPDATE_INFO', 'MARKUP_INFO'].includes(
            activeMenuRef.current,
          )
        ) {
          return;
        }

        const currentId = activePolylineIdRef.current;
        const newId = metadata.id;

        if (currentId === newId && activeToolRef.current === 'LINE') return;

        const freshData =
          polylinesDataRef.current.find((p) => p.id === newId) || metadata;

        const canProceed = clearAllExceptRef.current
          ? clearAllExceptRef.current('LINE', { ...freshData, type: 'line' })
          : true;
        if (!canProceed) return;

        if (onEnterEdit) onEnterEdit(freshData.id, 'HitLine Click Listener');
      });
      return hitLine;
    },
    [isDrawingLineRef],
  );

  const enterLineEditMode = useCallback(
    (id, caller = 'Unknown') => {
      if (clearAllExceptRef.current) {
        const lineData = polylinesDataRef.current.find((l) => l.id === id);
        clearAllExceptRef.current('LINE', lineData);
      }

      if (activePolylineIdRef.current !== id) {
        const lineData = polylinesDataRef.current.find((p) => p.id === id);
        if (lineData) {
          originalStateRef.current[id] = JSON.parse(JSON.stringify(lineData));
        }
      }

      if (
        activePolylineIdRef.current === id &&
        polylineRefs.current[id]?.visual?.getEditable()
      )
        return;

      const currentActive = activePolylineIdRef.current;
      if (currentActive !== null && currentActive !== id) {
        const oldObj = polylineRefs.current[currentActive];
        if (oldObj) {
          if (oldObj.visual) {
            oldObj.visual.setEditable(false);
            oldObj.visual.setDraggable(false);
          }
          if (!oldObj.hit) {
            const data = polylinesDataRef.current.find((p) => p.id === currentActive);
            if (data)
              oldObj.hit = createHitLine(
                mapInstance.current,
                data.path,
                data,
                enterLineEditModeRef.current,
              );
          }
        }
      }

      const obj = polylineRefs.current[id];
      if (!obj?.visual) return;

      const data =
        polylinesDataRef.current.find((p) => p.id === id) || obj.visual.metadata;
      if (!data) return;

      activePolylineIdRef.current = id;
      setActivePolylineId(id);
      setIsEditingPolyline(true);

      if (setSelectedMarkerDataRef.current) {
        setSelectedMarkerDataRef.current({ ...data, id, type: 'line' });
      }

      if (setDrawingStyles) {
        setDrawingStyles((prev) => ({
          ...prev,
          lineColor: data.color || DEFAULT_COLOR,
          lineWeight: data.weight || DEFAULT_WEIGHT,
          isDashed: data.isDashed || false,
          dashColor: data.dashColor || '#000000',
          dashLength: data.dashLength || 10,
        }));
      }

      if (obj.hit) {
        obj.hit.setOptions({ clickable: false });
        obj.hit.setMap(null);
        obj.hit = null;
      }

      if (
        activeToolRef.current === 'LINE' ||
        activeMenuRef.current === 'MARKUP_MARKERS'
      ) {
        obj.visual.setEditable(true);
      } else {
        obj.visual.setEditable(false);
      }

      if (editListenersRef.current.length > 0) {
        editListenersRef.current.forEach((l) =>
          window.google.maps.event.removeListener(l),
        );
        editListenersRef.current = [];
      }

      const path = obj.visual.getPath();

      const onUpdate = () => {
        if (setInteractionLock) setInteractionLock(true);
        window.__vertexInteractionLock = true;

        if (updateLockTimeoutRef.current) clearTimeout(updateLockTimeoutRef.current);

        updateLockTimeoutRef.current = setTimeout(() => {
          if (setInteractionLock) setInteractionLock(false);
          window.__vertexInteractionLock = false;
        }, 500);

        const updatedPathData = path.getArray().map((pt) => ({
          lat: typeof pt.lat === 'function' ? pt.lat() : pt.lat,
          lng: typeof pt.lng === 'function' ? pt.lng() : pt.lng,
        }));
        if (setSelectedMarkerDataRef.current)
          setSelectedMarkerDataRef.current((prev) =>
            prev
              ? { ...prev, path: updatedPathData }
              : { ...data, path: updatedPathData },
          );

        setPolylinesData((prev) =>
          prev.map((l) => (l.id === id ? { ...l, path: updatedPathData } : l)),
        );
      };

      editListenersRef.current.push(
        obj.visual.addListener('dragstart', () => {
          if (setInteractionLock) setInteractionLock(true);
          window.__vertexInteractionLock = true;
        }),
      );

      editListenersRef.current.push(
        path.addListener('set_at', (idx) => {
          const pt = path.getAt(idx);
          if (onDebugUpdate)
            onDebugUpdate({
              stage: 'VERTEX MOVED',
              points: path.getLength(),
              coords: `${pt.lat().toFixed(5)}, ${pt.lng().toFixed(5)}`,
            });
          onUpdate();
        }),
      );

      editListenersRef.current.push(
        path.addListener('insert_at', (idx) => {
          const pt = path.getAt(idx);
          if (onDebugUpdate)
            onDebugUpdate({
              stage: 'VERTEX ADDED',
              points: path.getLength(),
              coords: `${pt.lat().toFixed(5)}, ${pt.lng().toFixed(5)}`,
            });
          onUpdate();
        }),
      );

      editListenersRef.current.push(
        path.addListener('remove_at', (idx) => {
          if (onDebugUpdate)
            onDebugUpdate({ stage: 'VERTEX REMOVED', points: path.getLength() });
          onUpdate();
        }),
      );

      editListenersRef.current.push(obj.visual.addListener('dragend', onUpdate));
    },
    [
      mapInstance,
      createHitLine,
      setInteractionLock,
      onDebugUpdate,
      setPolylinesData,
      setDrawingStyles,
    ],
  );

  useEffect(() => {
    enterLineEditModeRef.current = enterLineEditMode;
  }, [enterLineEditMode]);

  useEffect(() => {
    const id = activePolylineIdRef.current;
    if (id && polylineRefs.current[id]?.visual) {
      const obj = polylineRefs.current[id];
      const isTargetTool = activeTool === 'LINE';
      obj.visual.setEditable(isTargetTool || activeMenuRef.current === 'MARKUP_MARKERS');
    }
  }, [activeTool]);

  useEffect(() => {
    if (editModeEnabled && activePolylineIdRef.current)
      enterLineEditMode(activePolylineIdRef.current, 'editModeEnabled Effect');
  }, [editModeEnabled, enterLineEditMode]);

  const loadPolylines = useCallback(
    (google) => {
      if (!mapInstance.current) return;
      const currentData = polylinesData || [];

      const today = new Date().toISOString().split('T')[0];
      const rSettings = restrictedSettingsRef.current;
      const isPermitted = isPermittedUserRef.current;

      const visibleLines = [];
      currentData.forEach((l) => {
        let displayData = { ...l };

        if (!isPermitted) {
          let vis = l.visibility;
          if (!vis) {
            if (l.publicFromDate && l.publicFromDate.trim() !== '')
              vis = `Public From ${l.publicFromDate}`;
            else if (l.isPrivate) vis = 'Private';
            else vis = 'Public';
          }

          if (vis === 'Private') return;
          if (vis && vis.startsWith('Public From')) {
            const datePart = vis.replace('Public From', '').trim();
            if (datePart && datePart > today) return;
          }

          if (vis === 'Restricted' && rSettings) {
            displayData.hoverImage = rSettings.RestrictedHoverImage || '';
            displayData.hoverContent = rSettings.RestrictedHoverText || '';
          }
        }
        visibleLines.push(displayData);
      });

      const currentIds = new Set(visibleLines.map((p) => p.id));
      Object.keys(polylineRefs.current).forEach((id) => {
        if (!currentIds.has(id) && id !== activePolylineIdRef.current) {
          const obj = polylineRefs.current[id];
          if (obj) {
            if (obj.visual) {
              obj.visual.setEditable(false);
              obj.visual.setDraggable(false);
              obj.visual.setOptions({ clickable: false });
              obj.visual.setMap(null);
            }
            if (obj.hit) {
              obj.hit.setOptions({ clickable: false });
              obj.hit.setMap(null);
            }
          }
          delete polylineRefs.current[id];
        }
      });

      visibleLines.forEach((l) => {
        if (!l.path?.length) return;

        let cleanPath = [];
        try {
          cleanPath = l.path
            .map((pt) => ({
              lat: parseFloat(getCoord(pt, 'lat')),
              lng: parseFloat(getCoord(pt, 'lng')),
            }))
            .filter((pt) => !isNaN(pt.lat) && !isNaN(pt.lng));
        } catch (e) {
          console.error('Invalid polyline path data:', l.path);
          return;
        }

        if (!cleanPath.length) return;

        let existingObj = polylineRefs.current[l.id];
        const shouldBeClickable = isClickableRef.current;

        if (existingObj) {
          try {
            const isEditingThis = activePolylineIdRef.current === l.id;
            if (existingObj.visual) {
              const oldMetadata = existingObj.visual.metadata || {};
              const oldColor = oldMetadata.color || DEFAULT_COLOR;
              const newColor = l.color || DEFAULT_COLOR;
              const oldWeight = parseInt(oldMetadata.weight, 10) || DEFAULT_WEIGHT;
              const newWeight = parseInt(l.weight, 10) || DEFAULT_WEIGHT;
              const oldClickable = existingObj.visual.get('clickable');

              const oldDashed = oldMetadata.isDashed || false;
              const oldDashColor = oldMetadata.dashColor || '#000000';
              const oldDashLength = oldMetadata.dashLength || 10;

              const styleChanged =
                oldColor !== newColor ||
                oldWeight !== newWeight ||
                oldClickable !== shouldBeClickable ||
                oldDashed !== l.isDashed ||
                oldDashColor !== l.dashColor ||
                oldDashLength !== l.dashLength;

              if (styleChanged) {
                const icons = getDashIcons(
                  l.isDashed,
                  l.dashColor,
                  l.dashLength,
                  newWeight,
                );
                existingObj.visual.setOptions({
                  clickable: shouldBeClickable,
                  strokeColor: newColor,
                  strokeWeight: newWeight,
                  icons: icons,
                });
              }

              if (!existingObj.visual.getMap())
                existingObj.visual.setMap(mapInstance.current);

              if (!isEditingThis) existingObj.visual.setPath(cleanPath);

              existingObj.visual.metadata = l;
            }

            if (existingObj.hit) {
              if (!existingObj.hit.getMap()) existingObj.hit.setMap(mapInstance.current);
              existingObj.hit.setPath(cleanPath);
              existingObj.hit.metadata = l;
              if (existingObj.hit.get('clickable') !== shouldBeClickable) {
                existingObj.hit.setOptions({ clickable: shouldBeClickable });
              }
            }
          } catch (updateErr) {
            console.error('Failed to update existing Polyline:', updateErr);
          }
          return;
        }

        try {
          const weight = parseInt(l.weight, 10) || DEFAULT_WEIGHT;
          const icons = getDashIcons(l.isDashed, l.dashColor, l.dashLength, weight);

          const visualLine = new google.maps.Polyline({
            path: cleanPath,
            map: mapInstance.current,
            strokeColor: l.color || DEFAULT_COLOR,
            strokeWeight: weight,
            icons: icons,
            zIndex: 10,
            clickable: shouldBeClickable,
          });

          const hitLine = createHitLine(
            mapInstance.current,
            cleanPath,
            l,
            enterLineEditModeRef.current,
          );

          if (l.id === activePolylineIdRef.current) {
            visualLine.setEditable(true);
            setIsEditingPolyline(true);
            const pathObj = visualLine.getPath();
            const onUpdate = () => {
              const updatedPathData = pathObj.getArray().map((pt) => ({
                lat: typeof pt.lat === 'function' ? pt.lat() : pt.lat,
                lng: typeof pt.lng === 'function' ? pt.lng() : pt.lng,
              }));
              if (setSelectedMarkerDataRef.current)
                setSelectedMarkerDataRef.current((prev) =>
                  prev
                    ? { ...prev, path: updatedPathData }
                    : { ...l, path: updatedPathData },
                );
            };
            editListenersRef.current.forEach((listener) =>
              window.google.maps.event.removeListener(listener),
            );
            editListenersRef.current = [];
            editListenersRef.current.push(
              pathObj.addListener('set_at', onUpdate),
              pathObj.addListener('insert_at', onUpdate),
              pathObj.addListener('remove_at', onUpdate),
              visualLine.addListener('dragend', onUpdate),
            );
          }

          polylineRefs.current[l.id] = { visual: visualLine, hit: hitLine };
        } catch (err) {
          console.error('Failed to instantiate Polyline geometry:', err);
        }
      });
    },
    [mapInstance, polylinesData, createHitLine],
  );

  const saveLineEdits = useCallback(
    async (groupDataPayload) => {
      let newPath;
      let id = groupDataPayload?.id || activePolylineIdRef.current;
      let isCompletingDraw = false;

      if (isDrawingLineRef.current) {
        const drawData = completePolyline(window.google, true);
        if (!drawData) return;
        newPath = drawData.path;
        id = drawData.id;
        isCompletingDraw = true;
      } else {
        if (!id) return;
        const obj = polylineRefs.current[id];
        if (!obj?.visual) return;
        newPath = obj.visual
          .getPath()
          .getArray()
          .map((p) => p.toJSON());
      }

      if (setIsSaving) setIsSaving(true);
      try {
        const cleanGroupData = sanitizeForFirestore({ ...groupDataPayload });
        const groupId = cleanGroupData.markerGroupID || id;

        if (!groupDataPayload.isSilentAutoSave) {
          const firestoreGroupData = { ...cleanGroupData };
          ROGUE_FIELDS.forEach((field) => (firestoreGroupData[field] = deleteField()));

          delete firestoreGroupData.markerID;
          delete firestoreGroupData.polygonID;
          firestoreGroupData.polylineID = id;

          const batch = writeBatch(db);
          batch.set(doc(db, 'markerGroups', groupId), firestoreGroupData, {
            merge: true,
          });

          const memoryLine = polylinesDataRef.current.find((p) => p.id === id) || {};
          const finalColor = memoryLine.color || cleanGroupData.color || '#00B0FF';
          const finalWeight =
            memoryLine.weight !== undefined
              ? memoryLine.weight
              : cleanGroupData.weight !== undefined
                ? cleanGroupData.weight
                : 4;
          const finalIsDashed = cleanGroupData.isDashed ?? memoryLine.isDashed ?? false;
          const finalDashColor =
            cleanGroupData.dashColor ?? memoryLine.dashColor ?? '#000000';
          const finalDashLength =
            cleanGroupData.dashLength ?? memoryLine.dashLength ?? 10;

          const geometryData = {
            path: newPath,
            color: finalColor,
            weight: finalWeight,
            isDashed: finalIsDashed,
            dashColor: finalDashColor,
            dashLength: finalDashLength,
            markerGroupID: groupId,
          };

          batch.set(doc(db, 'polylines', id), geometryData, { merge: true });
          await batch.commit();

          ROGUE_FIELDS.forEach((field) => delete cleanGroupData[field]);
          const dataWithId = {
            ...cleanGroupData,
            ...geometryData,
            id: id,
            type: 'line',
            isNew: false,
            isSaved: true,
          };
          if (
            cleanGroupData.publicFromDate &&
            typeof cleanGroupData.publicFromDate === 'object'
          )
            delete dataWithId.publicFromDate;
          delete dataWithId.isSilentAutoSave;

          originalStateRef.current[id] = JSON.parse(JSON.stringify(dataWithId));

          setPolylinesData((prev) => {
            const next = prev.find((p) => p.id === id)
              ? prev.map((p) => (p.id === id ? dataWithId : p))
              : [...prev, dataWithId];
            polylinesDataRef.current = next;
            return next;
          });

          if (isCompletingDraw) {
            activePolylineIdRef.current = id;
            setActivePolylineId(id);
            if (setSelectedMarkerDataRef.current)
              setSelectedMarkerDataRef.current(dataWithId);
            setTimeout(() => {
              if (enterLineEditModeRef.current)
                enterLineEditModeRef.current(id, 'Polyline Save Edits');
              const obj = polylineRefs.current[id];
              if (obj?.visual)
                obj.visual.metadata = {
                  ...obj.visual.metadata,
                  ...dataWithId,
                  isNew: false,
                  isSaved: true,
                };
            }, 100);
          } else {
            const obj = polylineRefs.current[id];
            if (obj?.visual)
              obj.visual.metadata = {
                ...obj.visual.metadata,
                ...dataWithId,
                isNew: false,
                isSaved: true,
              };
            if (enterLineEditModeRef.current)
              enterLineEditModeRef.current(id, 'Polyline Save Edits');
            if (setSelectedMarkerDataRef.current)
              setSelectedMarkerDataRef.current(dataWithId);
          }
        } else {
          const batch = writeBatch(db);
          batch.set(
            doc(db, 'markerGroups', groupId),
            { polylineID: id },
            { merge: true },
          );

          const memoryLine = polylinesDataRef.current.find((p) => p.id === id) || {};
          const geometryData = {
            path: newPath,
            color: memoryLine.color || '#00B0FF',
            weight: memoryLine.weight !== undefined ? memoryLine.weight : 4,
            isDashed: memoryLine.isDashed ?? false,
            dashColor: memoryLine.dashColor ?? '#000000',
            dashLength: memoryLine.dashLength ?? 10,
            markerGroupID: groupId,
          };
          batch.set(doc(db, 'polylines', id), geometryData, { merge: true });
          await batch.commit();

          const updatedDraft = {
            ...memoryLine,
            ...geometryData,
            id,
            type: 'line',
            isNew: false,
            isSaved: true,
          };
          originalStateRef.current[id] = JSON.parse(JSON.stringify(updatedDraft));

          const obj = polylineRefs.current[id];
          if (obj?.visual) {
            obj.visual.metadata = {
              ...obj.visual.metadata,
              ...updatedDraft,
            };
          }

          setPolylinesData((prev) => {
            const next = prev.find((p) => p.id === id)
              ? prev.map((p) => (p.id === id ? updatedDraft : p))
              : [...prev, updatedDraft];
            polylinesDataRef.current = next;
            return next;
          });
        }
      } catch (err) {
        console.error('Error updating line:', err);
      } finally {
        if (setIsSaving) setIsSaving(false);
      }
    },
    [completePolyline, setPolylinesData, setIsSaving, isDrawingLineRef],
  );

  const deletePolyline = useCallback(
    async (idToDelete) => {
      if (setTooltipRef.current) {
        setTooltipRef.current(null);
        setTimeout(() => setTooltipRef.current(null), 50);
      }
      const id = idToDelete?.id || idToDelete || activePolylineIdRef.current;
      if (!id) return;
      if (setIsSaving) setIsSaving(true);
      const obj = polylineRefs.current[id];

      if (obj) {
        if (obj.visual) {
          obj.visual.setEditable(false);
          obj.visual.setDraggable(false);
          obj.visual.setOptions({ clickable: false });
          if (window.google) window.google.maps.event.clearInstanceListeners(obj.visual);
          obj.visual.setMap(null);
        }
        if (obj.hit) {
          obj.hit.setOptions({ clickable: false });
          if (window.google) window.google.maps.event.clearInstanceListeners(obj.hit);
          obj.hit.setMap(null);
        }
        delete polylineRefs.current[id];
      }

      try {
        const existingData = polylinesDataRef.current.find((p) => p.id === id);
        let groupId = existingData?.markerGroupID;

        if (!groupId) {
          const snap = await getDoc(doc(db, 'polylines', id));
          if (snap.exists() && snap.data().markerGroupID) {
            groupId = snap.data().markerGroupID;
          }
        }
        groupId = groupId || id;

        const markers = window.__markerArray || [];
        const polygons = window.__polygonArray || [];
        const siblingMarker = markers.find(
          (m) => String(m.markerGroupID || m.id) === String(groupId),
        );
        const siblingPolygon = polygons.find(
          (p) => String(p.markerGroupID || p.id) === String(groupId),
        );

        const batch = writeBatch(db);
        batch.delete(doc(db, 'polylines', id));

        if (!siblingMarker && !siblingPolygon) {
          batch.delete(doc(db, 'markerGroups', groupId));

          let hoverImageUrl = existingData?.hoverImage;
          let photosArray = existingData?.photos || [];
          let docsArray = existingData?.documents || [];

          if (!hoverImageUrl || photosArray.length === 0 || docsArray.length === 0) {
            const groupSnap = await getDoc(doc(db, 'markerGroups', groupId));
            if (groupSnap.exists()) {
              const groupData = groupSnap.data();
              hoverImageUrl = hoverImageUrl || groupData.hoverImage;
              if (photosArray.length === 0 && groupData.photos)
                photosArray = groupData.photos;
              if (docsArray.length === 0 && groupData.documents)
                docsArray = groupData.documents;
            }
          }

          if (hoverImageUrl) await deleteStorageImage(hoverImageUrl);

          if (photosArray && photosArray.length > 0) {
            for (const photo of photosArray) {
              if (photo.url) await deleteStorageImage(photo.url);
            }
          }

          if (docsArray && docsArray.length > 0) {
            for (const docItem of docsArray) {
              if (docItem.url) await deleteStorageImage(docItem.url);
            }
          }
        } else {
          batch.set(
            doc(db, 'markerGroups', groupId),
            { polylineID: deleteField() },
            { merge: true },
          );
        }

        await batch.commit();

        setPolylinesData((prev) => {
          const next = prev.filter((p) => p.id !== id);
          polylinesDataRef.current = next;
          return next;
        });

        delete originalStateRef.current[id];

        setIsEditingPolyline(false);
        activePolylineIdRef.current = null;
        setActivePolylineId(null);
        if (setSelectedMarkerDataRef.current) setSelectedMarkerDataRef.current(null);
      } catch (error) {
        console.error('Error deleting line:', error);
      } finally {
        if (setIsSaving) setIsSaving(false);
      }
    },
    [setPolylinesData, setIsSaving],
  );

  const cancelLineEdit = useCallback(
    (reason = 'Unknown', clearGlobal = true) => {
      if (setTooltipRef.current) {
        setTooltipRef.current(null);
        setTimeout(() => setTooltipRef.current(null), 50);
      }
      if (editListenersRef.current.length > 0) {
        editListenersRef.current.forEach((l) =>
          window.google.maps.event.removeListener(l),
        );
        editListenersRef.current = [];
      }
      const id = activePolylineIdRef.current;

      if (id) {
        const obj = polylineRefs.current[id];
        if (obj?.visual) {
          const original =
            originalStateRef.current[id] ||
            polylinesDataRef.current.find((p) => p.id === id);

          if (!original) {
            if (obj.visual.metadata?.isSaved) {
              obj.visual.setEditable(false);
              obj.visual.setOptions({ clickable: isClickableRef.current });
            } else {
              if (obj.visual) {
                obj.visual.setEditable(false);
                obj.visual.setDraggable(false);
                obj.visual.setOptions({ clickable: false });
                if (window.google)
                  window.google.maps.event.clearInstanceListeners(obj.visual);
                obj.visual.setMap(null);
              }
              if (obj.hit) {
                obj.hit.setOptions({ clickable: false });
                if (window.google)
                  window.google.maps.event.clearInstanceListeners(obj.hit);
                obj.hit.setMap(null);
              }
              delete polylineRefs.current[id];
            }
          } else {
            obj.visual.setEditable(false);
            if (original.path) obj.visual.setPath(original.path);

            const weight =
              original.weight !== undefined ? original.weight : DEFAULT_WEIGHT;
            const icons = getDashIcons(
              original.isDashed,
              original.dashColor,
              original.dashLength,
              weight,
            );

            obj.visual.setOptions({
              clickable: isClickableRef.current,
              strokeColor: original.color || DEFAULT_COLOR,
              strokeWeight: weight,
              icons: icons,
            });

            if (!obj.hit) {
              obj.hit = createHitLine(
                mapInstance.current,
                original.path,
                original,
                enterLineEditModeRef.current,
              );
            } else {
              if (original.path) obj.hit.setPath(original.path);
              obj.hit.setOptions({ clickable: isClickableRef.current });
            }

            setPolylinesData((prev) => {
              const next = prev.map((p) => (p.id === id ? original : p));
              polylinesDataRef.current = next;
              return next;
            });
          }
        }
        delete originalStateRef.current[id];
      }
      setIsEditingPolyline(false);
      activePolylineIdRef.current = null;
      setActivePolylineId(null);
      if (clearGlobal && setSelectedMarkerDataRef.current)
        setSelectedMarkerDataRef.current(null);
    },
    [mapInstance, createHitLine, setPolylinesData],
  );

  const clearActivePolyline = useCallback(
    (forceDeselect = false, clearGlobal = true) => {
      if (activePolylineIdRef.current)
        cancelLineEdit('clearActivePolyline requested', clearGlobal);
    },
    [cancelLineEdit],
  );

  useEffect(() => {
    return () => {
      if (updateLockTimeoutRef.current) clearTimeout(updateLockTimeoutRef.current);
    };
  }, []);

  return {
    polylineRefs,
    isEditingPolyline,
    activePolylineId,
    activePolylineIdRef,
    isDrawingLine,
    isDrawingLineRef,
    loadPolylines,
    startDrawingLine,
    cancelDrawingLine,
    handleLineDrawClick,
    handleLineMouseMove,
    completePolyline,
    saveLineEdits,
    deletePolyline,
    cancelLineEdit,
    changePolylineColor,
    changePolylineWeight,
    changePolylineDash,
    clearActivePolyline,
    enterLineEditMode,
    setActivePolylineId,
    setPolylinesClickable,
  };
}
